import { z } from 'zod';

/** Backend cap on `lineItemOverrides` entries per retry-parse request. */
export const MAX_LINE_ITEM_OVERRIDES = 200;

/** Backend cap on the `dlnNr` override. */
export const MAX_DLN_NR_LENGTH = 255;

/** Backend cap on the `costCenterOverride` text. */
export const MAX_COST_CENTER_LENGTH = 255;

/**
 * Fields of a line-item override that carry an actual correction.
 * At least one must be present unless the override removes the line item or
 * only clears fields.
 */
const CORRECTION_FIELDS = [
  'grossWeight',
  'grossWeightMeasure',
  'orderedQuantity',
  'orderedQuantityMeasure',
  'productName',
  'quantity',
  'quantityType',
  'remainingQuantity',
  'remainingQuantityMeasure',
  'sellerAssignedId',
  'tareWeight',
  'tareWeightMeasure',
  'weight',
  'weightMeasure',
] as const;

/**
 * Logical fields the backend can clear (delete) from a line item. Clearing a
 * measurement drops the whole value+unit pair on the server; the unit halves
 * are deliberately not addressable, so a clear can never produce the
 * half-pair shape OCR validation rejects. Wire names: the ky case converter
 * renames object keys, not string values inside arrays, so these must
 * already be snake_case.
 */
const CLEARABLE_FIELDS = [
  'gross_weight',
  'ordered_quantity',
  'product_name',
  'quantity',
  'remaining_quantity',
  'tare_weight',
  'weight',
] as const;

export type ClearableField = (typeof CLEARABLE_FIELDS)[number];

/** Correction fields wiped by each clearable field (both pair halves). */
const CLEARED_CORRECTION_FIELDS: Record<
  ClearableField,
  readonly (typeof CORRECTION_FIELDS)[number][]
> = {
  gross_weight: ['grossWeight', 'grossWeightMeasure'],
  ordered_quantity: ['orderedQuantity', 'orderedQuantityMeasure'],
  product_name: ['productName'],
  quantity: ['quantity', 'quantityType'],
  remaining_quantity: ['remainingQuantity', 'remainingQuantityMeasure'],
  tare_weight: ['tareWeight', 'tareWeightMeasure'],
  weight: ['weight', 'weightMeasure'],
};

const zodLineItemOverrideBaseSchema = z.object({
  /** Append a new line item to the addressed logistics package. */
  add: z.boolean().optional(),
  /** Logical fields to delete from the addressed line item. */
  clear: z.array(z.enum(CLEARABLE_FIELDS)).min(1).optional(),
  grossWeight: z.number().optional(),
  grossWeightMeasure: z.string().min(1).optional(),
  /** Index into the package's `lineItem[]`; required unless `add`. */
  lineItemIndex: z.number().int().nonnegative().optional(),
  /** Index into `ocrJson` `transaction.logisticsPackage[]`. */
  logisticsPackageIndex: z.number().int().nonnegative(),
  orderedQuantity: z.number().optional(),
  orderedQuantityMeasure: z.string().min(1).optional(),
  /** Final index of the item in its package, applied after add/remove. */
  position: z.number().int().nonnegative().optional(),
  productName: z.string().trim().min(1).optional(),
  quantity: z.number().optional(),
  quantityType: z.string().min(1).optional(),
  remainingQuantity: z.number().optional(),
  remainingQuantityMeasure: z.string().min(1).optional(),
  /** Remove the addressed line item. */
  remove: z.boolean().optional(),
  /** The article number, `product.sellerAssignedId`. */
  sellerAssignedId: z.string().trim().min(1).optional(),
  tareWeight: z.number().optional(),
  tareWeightMeasure: z.string().min(1).optional(),
  weight: z.number().optional(),
  weightMeasure: z.string().min(1).optional(),
});

type LineItemOverrideInput = z.infer<typeof zodLineItemOverrideBaseSchema>;

function hasCorrectionField(override: LineItemOverrideInput) {
  return CORRECTION_FIELDS.some((field) => override[field] !== undefined);
}

/** Messages for broken `clear` lists, mirroring the backend model validator. */
function getClearIssues(override: LineItemOverrideInput): string[] {
  const { clear } = override;
  if (!clear) return [];

  const issues: string[] = [];
  if (override.add || override.remove) {
    issues.push(
      'An override that adds or removes a line item cannot clear fields.',
    );
  }
  if (new Set(clear).size !== clear.length) {
    issues.push('A field cannot be cleared twice.');
  }
  for (const name of clear) {
    if (
      CLEARED_CORRECTION_FIELDS[name].some(
        (field) => override[field] !== undefined,
      )
    ) {
      issues.push(
        `The field "${name}" cannot be corrected and cleared at the same time.`,
      );
    }
  }
  return issues;
}

export const zodLineItemOverrideSchema =
  zodLineItemOverrideBaseSchema.superRefine((override, context) => {
    function addIssue(message: string, path?: (string | number)[]) {
      context.addIssue({ code: 'custom', message, path });
    }

    if (override.add && override.remove) {
      addIssue('An override cannot add and remove at the same time.');
      return;
    }

    for (const message of getClearIssues(override)) {
      addIssue(message, ['clear']);
    }

    if (override.add) {
      if (override.lineItemIndex !== undefined) {
        addIssue('An added line item must not have a line item index.', [
          'lineItemIndex',
        ]);
      }
      if (!hasCorrectionField(override)) {
        addIssue('An added line item needs at least one correction field.');
      }
      return;
    }

    if (override.lineItemIndex === undefined) {
      addIssue('A line item index is required unless adding a line item.', [
        'lineItemIndex',
      ]);
    }
    if (
      override.remove &&
      (hasCorrectionField(override) || override.position !== undefined)
    ) {
      addIssue('A removed line item cannot have correction fields.');
    }
    if (
      !override.remove &&
      !hasCorrectionField(override) &&
      !override.clear &&
      override.position === undefined
    ) {
      addIssue('An override needs at least one corrected or cleared field.');
    }
  });

export type LineItemOverride = z.infer<typeof zodLineItemOverrideSchema>;

const zodTextOverrideValue = z.string().min(1).nullable().optional();

function hasAnyValue(override: Record<string, unknown>) {
  return Object.values(override).some((value) => value !== undefined);
}

const textOverrideMessage = 'An override must set at least one field.';

/**
 * Party text corrections, written onto the party's `legalOrganization`. A sent
 * key is written, an explicit `null` deletes it, an omitted key keeps the
 * parsed value.
 */
const zodPartyTextOverrideSchema = z
  .strictObject({
    buildingNumber: zodTextOverrideValue,
    city: zodTextOverrideValue,
    country: zodTextOverrideValue,
    issuerAssignedId: zodTextOverrideValue,
    name: zodTextOverrideValue,
    postCode: zodTextOverrideValue,
    streetName: zodTextOverrideValue,
  })
  .refine(hasAnyValue, { message: textOverrideMessage });

export type PartyTextOverride = z.infer<typeof zodPartyTextOverrideSchema>;

export type PartyTextField = keyof PartyTextOverride;

export const PARTY_TEXT_FIELDS = zodPartyTextOverrideSchema.keyof().options;

/** Wire party keys; the case converter sends `shipFrom` as `ship_from`. */
const PARTY_OVERRIDE_KEYS = [
  'buyer',
  'carrier',
  'issuer',
  'seller',
  'shipFrom',
  'shipTo',
  'trader',
] as const;

export type PartyOverrideKey = (typeof PARTY_OVERRIDE_KEYS)[number];

/** Same write/delete/keep rules as a party text correction. */
const zodSalesAccountOverrideSchema = z
  .strictObject({ id: zodTextOverrideValue, name: zodTextOverrideValue })
  .refine(hasAnyValue, { message: textOverrideMessage });

/**
 * Construction site address (`transaction.delivery.shipTo.tradeAddress`),
 * same write/delete/keep rules. The site matched before is not reused, so the
 * address is matched again; a `siteId` in the same request wins.
 */
const zodSiteTextOverrideSchema = z
  .strictObject({
    buildingNumber: zodTextOverrideValue,
    city: zodTextOverrideValue,
    country: zodTextOverrideValue,
    lineOne: zodTextOverrideValue,
    name: zodTextOverrideValue,
    postCode: zodTextOverrideValue,
    streetName: zodTextOverrideValue,
  })
  .refine(hasAnyValue, { message: textOverrideMessage });

export type SiteTextOverride = z.infer<typeof zodSiteTextOverrideSchema>;

export type SiteTextField = keyof SiteTextOverride;

export const SITE_TEXT_FIELDS = zodSiteTextOverrideSchema.keyof().options;

/** Set-only: the backend cannot clear a plate, and ignores `null`. */
const zodVehicleOverrideSchema = z
  .strictObject({
    licensePlate: z.string().min(1).optional(),
    licensePlateTrailer: z.string().min(1).optional(),
  })
  .refine(hasAnyValue, {
    message: 'A vehicle override must set at least one license plate.',
  });

export type VehicleOverride = z.infer<typeof zodVehicleOverrideSchema>;

/** Set-only, like the plates. */
const zodOrderReferenceOverrideSchema = z
  .strictObject({
    buyerOrder: z.string().min(1).optional(),
    sellerOrder: z.string().min(1).optional(),
  })
  .refine(hasAnyValue, {
    message: 'An order reference override must set at least one reference.',
  });

export type OrderReferenceOverride = z.infer<
  typeof zodOrderReferenceOverrideSchema
>;

const zodAiModelSchema = z.object({
  modelId: z.string().min(1),
  modelType: z.enum(['ai_foundry', 'auto', 'di']),
});

export type RetryParseAiModel = z.infer<typeof zodAiModelSchema>;

/** Backend cap on the case note. */
export const MAX_NOTE_LENGTH = 500;

/**
 * Optional JSON body for `POST /vadmin/parsing_cases/{case_id}/actions/retry_parse`.
 *
 * When `ocrJson` is set, the parser uses it directly and skips the AI OCR call;
 * otherwise the full OCR pipeline re-runs from the source RAW blob.
 * `lineItemOverrides` are targeted corrections applied to the stored OCR
 * document — only supported when the case reports `supportsLineItemOverrides`.
 *
 * Independent party overrides: `buyerId`, `shipToId`, `shipFromId`, `sellerId`, `issuerId`,
 * `traderId`, `carrierId` each map to a specific party role. `supplierId` is kept for backwards-compatibility but
 * is no longer sent by the frontend (granular fields cover its former fan-out).
 */
export const zodRetryParseParsingCaseBodySchema = z
  .object({
    accountingReferenceId: z.uuid().nullish(),
    /**
     * Forces a re-OCR of the original PDF with these models. Never combined
     * with `ocrJson` or `lineItemOverrides`: the re-OCR regenerates the very
     * document those would edit.
     */
    aiModel: z.array(zodAiModelSchema).min(1).optional(),
    buyerId: z.uuid().nullish(),
    carrierId: z.uuid().nullish(),
    /**
     * Cost center text written onto every line item, matched again on the
     * retry. Set only: the backend cannot clear it.
     */
    costCenterOverride: z
      .string()
      .trim()
      .min(1)
      .max(MAX_COST_CENTER_LENGTH)
      .optional(),
    /** ISO `YYYY-MM-DD`, the shape a native date input produces. */
    dlnDate: z.iso.date().optional(),
    /** 1–255 chars, mirroring the backend; blank is omitted, never sent. */
    dlnNr: z.string().min(1).max(MAX_DLN_NR_LENGTH).optional(),
    issuerId: z.uuid().nullish(),
    lineItemOverrides: z
      .array(zodLineItemOverrideSchema)
      .min(1)
      .max(MAX_LINE_ITEM_OVERRIDES)
      .optional(),
    /** Replaces the case note; omitted keeps the existing one. */
    note: z.string().max(MAX_NOTE_LENGTH).optional(),
    ocrJson: z.record(z.string(), z.unknown()).nullish(),
    /** `agreement.buyerOrder` / `sellerOrder`, written onto every line item. */
    orderReferenceOverride: zodOrderReferenceOverrideSchema.optional(),
    /** Party text corrections; a party id override for the same party wins. */
    partyOverrides: z
      .partialRecord(z.enum(PARTY_OVERRIDE_KEYS), zodPartyTextOverrideSchema)
      .refine(hasAnyValue, {
        message: 'Party overrides must name at least one party.',
      })
      .optional(),
    /** `transaction.settlement.salesAccount` of the OCR document. */
    salesAccountOverride: zodSalesAccountOverrideSchema.optional(),
    sellerId: z.uuid().nullish(),
    shipFromId: z.uuid().nullish(),
    shipToId: z.uuid().nullish(),
    siteId: z.uuid().nullish(),
    siteOverride: zodSiteTextOverrideSchema.optional(),
    supplierId: z.uuid().nullish(),
    traderId: z.uuid().nullish(),
    vehicleOverride: zodVehicleOverrideSchema.optional(),
  })
  .superRefine((body, context) => {
    if (body.aiModel && (body.ocrJson || body.lineItemOverrides)) {
      context.addIssue({
        code: 'custom',
        message:
          'aiModel cannot be combined with ocrJson or lineItemOverrides.',
        path: ['aiModel'],
      });
    }

    const seenAddresses = new Set<string>();

    body.lineItemOverrides?.forEach((override, index) => {
      // `add` overrides address no existing line item and may repeat a package.
      if (override.add || override.lineItemIndex === undefined) return;

      const address = `${override.logisticsPackageIndex}:${override.lineItemIndex}`;
      if (seenAddresses.has(address)) {
        context.addIssue({
          code: 'custom',
          message: 'Overrides must not target the same line item twice.',
          path: ['lineItemOverrides', index],
        });
      }
      seenAddresses.add(address);
    });
  });
